"use client"

import Image from 'next/image';
import logo from '../img/logo.svg';
import logo_dark from '../img/logo_dark.svg';
import {Link} from "../../src/apps/front/i18n/navigation";
import Menu from "../../src/apps/front/components/Menu";
import React, {useEffect, useState} from "react";
import {ChevronDown} from "lucide-react";
import {Settings} from "../settings";
import {usePathname} from "next/navigation";
import {hasCloneMethod} from "fast-check";

export default function HeaderClient({locale, menu}) {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isLangSwitcherOpen, setLangSwitcherOpen] = useState(false);
    const langSwitcherRef = React.useRef(null);
    const [hasScrolled, setHasScrolled] = useState(false);
    const [isScrollingUp, setIsScrollingUp] = useState(false);
    const [lastScrollY, setLastScrollY] = useState(0);

    useEffect(() => {
        setIsMenuOpen(false);
    }, [usePathname()]);

    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY;
            setHasScrolled(scrollPosition > 200);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        function handleClickOutside(event) {
            if (langSwitcherRef.current && !langSwitcherRef.current.contains(event.target)) {
                setLangSwitcherOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY;

            // Check direction of scrolling
            setIsScrollingUp(scrollPosition < lastScrollY);
            setLastScrollY(scrollPosition);

            setHasScrolled(scrollPosition > 200);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [lastScrollY]);

    const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
    const toggleLangSwitcher = () => setLangSwitcherOpen(!isLangSwitcherOpen);

    return (
        <header className={`py-1 mb-12 transition-transform duration-500 ${isMenuOpen ? 'fixed top-0 left-0 w-full z-50' : hasScrolled && !isScrollingUp ? 'lg:-translate-y-full' : isScrollingUp ? 'lg:sticky lg:top-0 lg:z-999 bg-white/80 backdrop-blur-lg lg:translate-y-0' : 'relative'}`}>
            <div className="px-6 py-4 lg:px-4 lg:py-3 max-w-(--header-container-width) mx-auto">
                <div className="flex justify-between items-center">
                    <Link href="/" className={`${isMenuOpen ? 'relative z-2' : ''}`}>
                        <Image
                            className={`${isMenuOpen ? 'hidden lg:inline-block' : 'inline-block'}`}
                            src={logo}
                            width={140}
                            height={56}
                            alt="logo"
                        />
                        <Image
                            className={`${isMenuOpen ? 'inline-block lg:hidden' : 'hidden'}`}
                            src={logo_dark}
                            width={140}
                            height={56}
                            alt="logo dark"
                        />
                    </Link>

                    <Menu
                        items={menu.translations[0].items}
                        activeClassName="after:opacity-100"
                        linkClassName="relative py-2 after:content-[''] after:opacity-0 after:absolute after:h-[3px] after:bg-primary after:right-0 after:w-full after:bottom-0 hover:after:opacity-100"
                        className={`
                    tracking-normal
                    flex
                    gap-10
                    flex-col
                    align-center
                    text-center
                    absolute
                    bg-inverse
                    w-full
                    h-screen
                    left-0
                    top-0
                    text-body-inverse
                    p-8
                    pt-32
                    text-h-d-subheading
                    lg:relative
                    lg:left-[initial]
                    lg:top-[initial]
                    lg:bg-transparent
                    lg:flex
                    lg:flex-row
                    lg:w-[initial]
                    lg:h-[initial]
                    lg:gap-4
                    lg:p-0
                    lg:font-medium
                    lg:text-heading
                    lg:text-t-d-menu
                    xl:gap-7
                    ${isMenuOpen ? 'z-1' : 'hidden'}
                    `}
                    />

                    <div className="flex gap-9">
                        <div
                            className={`fixed w-full left-0 items-center justify-center bottom-15 gap-12 sm:relative sm:left-[initial] sm:w-[initial] sm:bottom-[initial] sm:gap-6 xl:gap-12 ${isMenuOpen ? 'z-2 flex sm:hidden lg:flex' : 'hidden sm:flex'}`}>
                            <div ref={langSwitcherRef}>
                                <div
                                    className={`flex items-center gap-0.5 tracking-normal cursor-pointer p-4 lg:p-2.5 rounded-t-lg ${isLangSwitcherOpen ? 'relative z-1' : ''}`}
                                    onClick={toggleLangSwitcher}>
                                    <span
                                        className={`sm:text-heading uppercase ${isLangSwitcherOpen ? 'text-heading' : 'text-body-inverse'}`}>{locale}</span>
                                    <ChevronDown
                                        className={`text-icon-default ${isLangSwitcherOpen ? 'rotate-180' : ''}`}/>
                                </div>
                                <ul className={`absolute max-w-20 flex-col p-4 lg:p-2.5 pt-[59px] top-0 rounded-lg gap-4 lg:gap-3 bg-blue-50 w-full shadow-[4px_4px_19px_rgba(0,0,0,O.1)] ${isLangSwitcherOpen ? 'flex' : 'hidden'}`}>
                                    <li className="hidden lg:block">&nbsp;</li>
                                    {Settings.languages.map(lang => (
                                        lang.slice(0, 2) === locale ? null :
                                            <li key={lang} className="uppercase">
                                                <Link href="/" locale={lang.slice(0, 2)}>
                                                    {lang.slice(0, 2)}
                                                </Link>
                                            </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        <button id="hamburger" onClick={toggleMenu} type="button"
                                className={`gap-2 flex justify-between items-center cursor-pointer lg:hidden ${isMenuOpen ? 'z-2 relative' : ''} ${hasScrolled && !isMenuOpen ? 'fixed z-50 top-7 right-4 rounded-xl bg-primary-button text-white p-3 shadow-[0_4px_15px_#78160A4D]' : 'py-4'}`}>
                            <span
                                className={`font-medium tracking-normal ${isMenuOpen ? 'text-body-inverse' : ''}`}>Menu</span>
                            <div className={`
                        size-6
                        relative
                        w-6
                        before:content-['']
                        before:absolute
                        before:h-[2px]
                        before:bg-primary
                        before:right-0
                        after:content-['']
                        after:absolute
                        after:h-[2px]
                        after:bg-primary
                        after:right-0
                        ${isMenuOpen ? 'h-0 bg-transparent before:w-6 before:translate-y-0 before:rotate-45 after:translate-y-0 after:-rotate-45 after:w-6' : 'h-[2px] bg-primary before:-translate-y-2 after:translate-y-2 before:w-4 after:w-4'}
                        ${hasScrolled && !isMenuOpen ? 'bg-white before:bg-white after:bg-white' : ''}
                        `}></div>
                        </button>
                    </div>
                </div>
            </div>
        </header>
    )
}